<template>
    <div class="tournament-editor">
        <div class="tournament-editor__controls">
            <div class="tournament-editor__filters">
                <div class="tournament-editor__filter-group">
                    <label class="tournament-editor__label">Ліга</label>
                    <select v-model="selectedLeagueId" class="tournament-editor__select" @change="onLeagueChange">
                        <option :value="null" disabled>Оберіть лігу</option>
                        <option v-for="league in leagues" :key="league.id" :value="league.id">{{ league.label }}</option>
                    </select>
                </div>
                <div class="tournament-editor__filter-group">
                    <label class="tournament-editor__label">Сезон</label>
                    <select v-model="selectedSeason" class="tournament-editor__select" @change="loadStandings">
                        <option :value="null" disabled>Оберіть сезон</option>
                        <option v-for="season in seasons" :key="season" :value="season">{{ season }}</option>
                    </select>
                </div>
            </div>
            <div class="tournament-editor__actions">
                <button class="tournament-editor__btn tournament-editor__btn--add" @click="addTeamRow">
                    + Додати команду
                </button>
                <button class="tournament-editor__btn tournament-editor__btn--save" @click="saveStandings" :disabled="saving">
                    {{ saving ? 'Збереження...' : 'Зберегти таблицю' }}
                </button>
            </div>
        </div>

        <div class="tournament-editor__table-wrapper" v-if="standings.length > 0">
            <table class="tournament-editor__table">
                <thead>
                    <tr>
                        <th class="tournament-editor__th">#</th>
                        <th class="tournament-editor__th tournament-editor__th--team">Команда</th>
                        <th class="tournament-editor__th">Лого URL</th>
                        <th class="tournament-editor__th">І</th>
                        <th class="tournament-editor__th">В</th>
                        <th class="tournament-editor__th">Н</th>
                        <th class="tournament-editor__th">П</th>
                        <th class="tournament-editor__th">ГЗ</th>
                        <th class="tournament-editor__th">ГП</th>
                        <th class="tournament-editor__th">ОЧ.</th>
                        <th class="tournament-editor__th">Форма (5 остан.)</th>
                        <th class="tournament-editor__th"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(team, index) in standings" :key="index">
                        <td class="tournament-editor__td">{{ index + 1 }}</td>
                        <td class="tournament-editor__td">
                            <input v-model="team.teamName" class="tournament-editor__input tournament-editor__input--wide" placeholder="Назва команди" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model="team.teamLogo" class="tournament-editor__input" placeholder="URL лого" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.played" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.wins" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" @input="recalcPoints(team)" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.draws" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" @input="recalcPoints(team)" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.losses" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.goalsFor" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" />
                        </td>
                        <td class="tournament-editor__td">
                            <input v-model.number="team.goalsAgainst" type="number" class="tournament-editor__input tournament-editor__input--num" min="0" />
                        </td>
                        <td class="tournament-editor__td">
                            <span class="tournament-editor__points">{{ team.points }}</span>
                        </td>
                        <td class="tournament-editor__td">
                            <div class="tournament-editor__form-editor">
                                <button v-for="(result, i) in team.form" :key="i"
                                    class="tournament-editor__form-btn"
                                    :class="{
                                        'tournament-editor__form-btn--win': result === 'W',
                                        'tournament-editor__form-btn--draw': result === 'D',
                                        'tournament-editor__form-btn--loss': result === 'L'
                                    }"
                                    @click="cycleFormResult(team, i)">
                                    {{ result === 'W' ? 'В' : result === 'D' ? 'Н' : 'П' }}
                                </button>
                            </div>
                        </td>
                        <td class="tournament-editor__td">
                            <button class="tournament-editor__remove-btn" @click="removeTeamRow(index)">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6H5H21" stroke="#DE3A73" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#DE3A73" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="tournament-editor__empty">
            <p class="text-[#8C8C8C]" v-if="selectedLeagueId && selectedSeason">
                Немає даних. Натисніть "Додати команду" для створення таблиці.
            </p>
            <p class="text-[#8C8C8C]" v-else>
                Оберіть лігу та сезон для редагування таблиці.
            </p>
        </div>

        <div v-if="saveMessage" class="tournament-editor__message" :class="{'tournament-editor__message--error': saveError}">
            {{ saveMessage }}
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { getAllLeagues, getAllTeams, getTournamentStandings, saveTournamentStandings } from '@/services/apiRequests'

export default {
    name: 'TournamentTableEditor',

    setup(_, { emit }) {
        const leagues = ref([])
        const seasons = ref([])
        const allTeams = ref([])
        const selectedLeagueId = ref(null)
        const selectedSeason = ref(null)
        const standings = ref([])
        const saving = ref(false)
        const saveMessage = ref('')
        const saveError = ref(false)

        emit('updatePageName', 'Турнірна таблиця')

        const onLeagueChange = () => {
            const leagueTeams = allTeams.value.filter(t => t.league === selectedLeagueId.value)
            seasons.value = [...new Set(leagueTeams.map(t => t.season))]
            selectedSeason.value = null
            standings.value = []
        }

        const loadStandings = async () => {
            if (!selectedLeagueId.value || !selectedSeason.value) return
            try {
                const data = await getTournamentStandings(selectedLeagueId.value, selectedSeason.value)
                standings.value = (data || []).map(team => ({
                    ...team,
                    form: team.form || ['W', 'W', 'W', 'D', 'L']
                }))
            } catch (e) {
                standings.value = []
            }
        }

        const addTeamRow = () => {
            standings.value.push({
                teamName: '',
                teamLogo: '',
                played: 0,
                wins: 0,
                draws: 0,
                losses: 0,
                goalsFor: 0,
                goalsAgainst: 0,
                points: 0,
                form: ['W', 'W', 'W', 'D', 'L']
            })
        }

        const removeTeamRow = (index) => {
            standings.value.splice(index, 1)
        }

        const recalcPoints = (team) => {
            team.points = (team.wins || 0) * 3 + (team.draws || 0) * 1
        }

        const cycleFormResult = (team, index) => {
            const cycle = ['W', 'D', 'L']
            const current = team.form[index]
            const nextIdx = (cycle.indexOf(current) + 1) % cycle.length
            team.form[index] = cycle[nextIdx]
        }

        const saveStandings = async () => {
            if (!selectedLeagueId.value || !selectedSeason.value) {
                saveMessage.value = 'Оберіть лігу та сезон'
                saveError.value = true
                return
            }

            saving.value = true
            saveMessage.value = ''
            saveError.value = false

            try {
                await saveTournamentStandings(selectedLeagueId.value, selectedSeason.value, standings.value)
                saveMessage.value = 'Таблицю збережено успішно!'
                saveError.value = false
            } catch (e) {
                saveMessage.value = 'Помилка збереження таблиці'
                saveError.value = true
            } finally {
                saving.value = false
                setTimeout(() => { saveMessage.value = '' }, 3000)
            }
        }

        onMounted(async () => {
            try {
                const leaguesData = await getAllLeagues()
                leagues.value = leaguesData.map(l => ({ id: l.league_id, label: l.league_name }))
                
                const teamsData = await getAllTeams()
                allTeams.value = teamsData.map(t => ({
                    id: t.team_id,
                    label: t.team_name,
                    league: t.league_id,
                    season: t.season
                }))
            } catch (e) {
                leagues.value = []
                allTeams.value = []
            }
        })

        return {
            leagues,
            seasons,
            selectedLeagueId,
            selectedSeason,
            standings,
            saving,
            saveMessage,
            saveError,
            onLeagueChange,
            loadStandings,
            addTeamRow,
            removeTeamRow,
            recalcPoints,
            cycleFormResult,
            saveStandings
        }
    }
}
</script>

<style scoped>
.tournament-editor {
    padding: 24px;
    background: #141414;
    min-height: 100vh;
}

.tournament-editor__controls {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 16px;
}

.tournament-editor__filters {
    display: flex;
    gap: 16px;
}

.tournament-editor__filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.tournament-editor__label {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.tournament-editor__select {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #fff;
    padding: 9px 14px;
    border-radius: 10px;
    font-size: 13px;
    outline: none;
    min-width: 200px;
    cursor: pointer;
    transition: border-color 0.2s;
}

.tournament-editor__select:hover,
.tournament-editor__select:focus {
    border-color: rgba(197, 144, 20, 0.4);
}

.tournament-editor__actions {
    display: flex;
    gap: 12px;
}

.tournament-editor__btn {
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.tournament-editor__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.tournament-editor__btn--add {
    background: rgba(143, 196, 8, 0.08);
    color: #8FC408;
    border: 1px solid rgba(143, 196, 8, 0.3);
}

.tournament-editor__btn--add:hover {
    background: rgba(143, 196, 8, 0.15);
    border-color: rgba(143, 196, 8, 0.5);
}

.tournament-editor__btn--save {
    background: linear-gradient(135deg, #339989 0%, #2b7d70 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(51, 153, 137, 0.3);
}

.tournament-editor__btn--save:hover {
    box-shadow: 0 4px 16px rgba(51, 153, 137, 0.4);
    transform: translateY(-1px);
}

.tournament-editor__table-wrapper {
    overflow-x: auto;
    background: linear-gradient(180deg, #1c1c1e 0%, #161618 100%);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.04);
}

.tournament-editor__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.tournament-editor__th {
    color: rgba(255, 255, 255, 0.35);
    font-weight: 600;
    text-align: center;
    padding: 12px 8px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    white-space: nowrap;
}

.tournament-editor__th--team {
    text-align: left;
    min-width: 150px;
}

.tournament-editor__td {
    padding: 8px 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    text-align: center;
    vertical-align: middle;
}

.tournament-editor__input {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 12px;
    width: 60px;
    text-align: center;
    outline: none;
    transition: border-color 0.2s;
}

.tournament-editor__input:focus {
    border-color: rgba(51, 153, 137, 0.5);
    background: rgba(255, 255, 255, 0.06);
}

.tournament-editor__input--wide {
    width: 150px;
    text-align: left;
}

.tournament-editor__input--num {
    width: 50px;
}

.tournament-editor__points {
    color: #fff;
    font-weight: 800;
    font-size: 15px;
}

.tournament-editor__form-editor {
    display: flex;
    gap: 4px;
    justify-content: center;
}

.tournament-editor__form-btn {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: none;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.08);
    transition: transform 0.15s, box-shadow 0.15s;
}

.tournament-editor__form-btn:hover {
    transform: scale(1.1);
}

.tournament-editor__form-btn--win {
    background: #339989;
    box-shadow: 0 2px 6px rgba(51, 153, 137, 0.4);
}

.tournament-editor__form-btn--draw {
    background: #C59014;
    box-shadow: 0 2px 6px rgba(197, 144, 20, 0.4);
}

.tournament-editor__form-btn--loss {
    background: #DE3A73;
    box-shadow: 0 2px 6px rgba(222, 58, 115, 0.4);
}

.tournament-editor__remove-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    opacity: 0.5;
    transition: opacity 0.2s, transform 0.15s;
    border-radius: 6px;
}

.tournament-editor__remove-btn:hover {
    opacity: 1;
    transform: scale(1.1);
    background: rgba(222, 58, 115, 0.1);
}

.tournament-editor__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 240px;
    background: linear-gradient(180deg, #1c1c1e 0%, #161618 100%);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.25);
    font-size: 13px;
}

.tournament-editor__message {
    margin-top: 16px;
    padding: 12px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    background: rgba(51, 153, 137, 0.1);
    color: #339989;
    border: 1px solid rgba(51, 153, 137, 0.2);
}

.tournament-editor__message--error {
    background: rgba(222, 58, 115, 0.1);
    color: #DE3A73;
    border-color: rgba(222, 58, 115, 0.2);
}
</style>
